/* tslint:disable:max-line-length */
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {Classifications} from '@docma-mm-core/classification/classification';
import {ClassificationService} from '@docma-mm-core/classification/classification.service';
import {Contact} from '@docma-mm-core/contact/contact';
import {ContactService} from '@docma-mm-core/contact/contact.service';
import {Craft} from '@docma-mm-core/craft/craft';
import {CraftService} from '@docma-mm-core/craft/craft.service';
import {FieldMinimal} from '@docma-mm-core/field/field';
import {FieldService} from '@docma-mm-core/field/field.service';
import {Layout} from '@docma-mm-core/layout/layout';
import {LayoutApiService} from '@docma-mm-core/layout/layout-api.service';
import {OrderService} from '@docma-mm-core/order/order.service';
import {ProjectUserPermissions} from '@docma-mm-core/permission/project-user-permissions';
import {Project} from '@docma-mm-core/project/project';
import {ProjectHttpService} from '@docma-mm-core/project/project-http.service';
import {Status} from '@docma-mm-core/status/status';
import {StatusService} from '@docma-mm-core/status/status.service';
import {PermissionService} from '@docma-mm-core/permission/permission.service';
import {Structure} from '@docma-mm-core/structure/structure';
import {StructureService} from '@docma-mm-core/structure/structure.service';
import {UserDefined, UserDefineds} from '@docma-mm-core/user-defined/user-defined';
import {UserDefinedApiService} from '@docma-mm-core/user-defined/user-defined-api.service';
import {DocumentType} from '@docma-mm-core/document-type/document-type';
import {DocumentTypeService} from '@docma-mm-core/document-type/document-type.service';
import {defer, firstValueFrom, Observable} from 'rxjs';
import {MmDbService} from '../mm-db/mm-db-service';
import {MmProjectDbCollections} from '../mm-db/mm-project-db-schema';
import {CompanyService} from '@docma-mm-core/company/company.service';
import {Company} from '@docma-mm-core/company/company';
import {PlanData} from '@docma-mm-core/plan/plan';
import {PlanService} from '@docma-mm-core/plan/plan.service';
import {ProjectSettingsService} from '@docma-mm-core/project-settings/project-settings.service';
import {ProjectSetting} from '@docma-mm-core/project-settings/project-setting';
import {Attribute} from '@docma-mm-core/attribute/attribute';
import {AttributeApiService} from '@docma-mm-core/attribute/attribute-api.service';
import {AttributeGroupApiService} from '@docma-mm-core/attribute-group/attribute-group-api.service';
import {AttributeGroup} from '@docma-mm-core/attribute-group/attribute-group';
import {AttributeGroupFile} from '@docma-mm-core/attribute-group/attribute-group-file';
import {constants} from '@docma-mm-core/constants';
import {DelayApiService} from '@docma-mm-core/delay/delay-api.service';
import {Delay} from '@docma-mm-core/delay/delay';
import {Order} from '@docma-mm-core/order/order';
import {Pool} from '@docma-mm-core/pool/pool';
import {PoolService} from '@docma-mm-core/pool/pool.service';
import {IOfflineService} from '../mm-offline/interfaces/offline-service.interface';
import {OFFLINE_SERVICE} from '../mm-offline/interfaces/offline-service.token';
import {IMmConfig} from '@docma-mm-core/project/project.interface';
import {ConfigProvider} from '@mydocma/common';
import {PlatformModeStateService} from '../mm-offline/services/platform-mode-state.service';
import {LocalLayoutService} from '../mm-offline/services/local-layout.service';
import {Phase} from '@docma-mm-core/phase/phase';
import {Integration} from '@docma-mm-core/integration/integration';
import {PhaseApiService} from '@docma-mm-core/phase/phase-api.service';
import {IntegrationApiService} from '@docma-mm-core/integration/integration-api.service';
import {IntegrationFile} from '@docma-mm-core/integration/integration-file';
import {ProjectIntegrationApiService} from '@docma-mm-core/project-integration/project-integration.service';
import {Logger} from '@mydocma/common/src/lib/logger';

@Injectable()
export class MmProjectOfflineService implements OnDestroy {

  constructor(private readonly mmDbService: MmDbService,
              private readonly structureService: StructureService,
              private readonly contactService: ContactService,
              private readonly fieldService: FieldService,
              private readonly orderService: OrderService,
              private readonly httpProjectService: ProjectHttpService,
              private readonly craftService: CraftService,
              private readonly userDefinedService: UserDefinedApiService,
              private readonly statusService: StatusService,
              private readonly permissionsService: PermissionService,
              private readonly classificationService: ClassificationService,
              private readonly documentTypeService: DocumentTypeService,
              private readonly phaseApiService: PhaseApiService,
              private readonly integrationApiService: IntegrationApiService,
              private readonly companiesService: CompanyService,
              private readonly layoutService: LayoutApiService,
              private readonly planService: PlanService,
              private readonly projectSettingsService: ProjectSettingsService,
              private readonly attributeService: AttributeApiService,
              private readonly attributeGroupService: AttributeGroupApiService,
              private readonly delayApiService: DelayApiService,
              private readonly poolService: PoolService,
              private readonly platformModeStateService: PlatformModeStateService,
              @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
              private configService: ConfigProvider<IMmConfig>,
              private localLayoutService: LocalLayoutService,
              private readonly projectIntegrationApiService: ProjectIntegrationApiService,
              private readonly logger: Logger) {
  }

  ngOnDestroy(): void {
  }

  public isNewDb(mydocmaId: string) {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaId);
      return await db.isNew();
    });
  }

  public getProject(mydocmaId: string, forceRefresh: boolean = false): Observable<Project> {

    return defer(async () => {

      if (!mydocmaId) {
        throw new Error('Project mydocmaId is not set');
      }

      const db = await this.mmDbService.getProjectDb(mydocmaId);
      const projects = await db.getAll(MmProjectDbCollections.project as never);
      let mmProject = projects ? projects[0] : null;

      if (!forceRefresh) {
        return mmProject;
      }

      try {
        mmProject = await this.httpProjectService.getProjectByMydocmaId(mydocmaId).toPromise();
        if (mmProject.Structure) {
          mmProject.Structure.Name1 = mmProject.Name1;
          mmProject.Structure.Name2 = mmProject.Name2;
        }
      } catch (err) {
        this.logger.error(err, '', 'MmProjectOfflineService');
        throw new Error('Cannot download project');
      }
      await db.setAll(MmProjectDbCollections.project as never, [mmProject]);
      return mmProject;
    });
  }

  public getLayouts(project: Project, forceRefresh: boolean = false): Observable<Layout[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let layouts;
      if (!forceRefresh) {
        layouts = await db.getAll(MmProjectDbCollections.layouts as never);
        return layouts;
      }
      layouts = await this.layoutService.getProjectLayouts(project.Id, constants.MaxInt).toPromise();

      if (this.platformModeStateService.isLocalModeEnabled) {
        const localLayout = this.localLayoutService.getLocalLayout();

        layouts.Data.push(localLayout);
      }

      await db.setAll(MmProjectDbCollections.layouts as never, layouts.Data);
      return layouts.Data;
    });
  }

  public getLayoutsByAddedOrUpdated(project: Project, layout: Layout, forceRefresh: boolean = false): Observable<Layout[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const layouts: Layout[] = await db.getAll(MmProjectDbCollections.layouts as never);
      // const reLoadedLayouts = await this.layoutService.getProjectLayouts(project.Id, 1000).toPromise();
      const existingLayoutIdx = layouts.findIndex(l => l.Id === layout.Id);


      const newOrUpdatedLayout = (({ LayoutFields, LayoutFilters, ...object }) => object)(layout);

      if (existingLayoutIdx === -1) {
        /* ADD NEW LAYOUT */
        layouts.push(newOrUpdatedLayout);
      }
      else {
        /* UPDATE EXISTING LAYOUT*/
        layouts.splice(existingLayoutIdx, 1);
        layouts.splice(existingLayoutIdx, 0, newOrUpdatedLayout);
      }

      await db.set(MmProjectDbCollections.layouts as never, newOrUpdatedLayout);
      return layouts;
    });
  }

  public getLayoutsByDeleted(project: Project, layoutId: number, forceRefresh: boolean = false): Observable<Layout[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const layouts: Layout[] = await db.getAll(MmProjectDbCollections.layouts as never);
      // const reLoadedLayouts = await this.layoutService.getProjectLayouts(project.Id, 1000).toPromise();
      const existingLayoutIdx = layouts.findIndex(l => l.Id === layoutId);

      if (existingLayoutIdx !== -1) {
        /* REMOVE DELETED LAYOUT */
        layouts.splice(existingLayoutIdx, 1);
      }

      await db.remove(MmProjectDbCollections.layouts as never, layoutId);
      return layouts;
    });
  }

  public getPools(project: Project, forceRefresh: boolean = false): Observable<Pool[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let pools;
      if (!forceRefresh) {
        pools = await db.getAll(MmProjectDbCollections.pools as never);
        return pools;
      }
      pools = await this.poolService.getPools(project.Id, constants.MaxInt).toPromise();

      await db.setAll(MmProjectDbCollections.pools as never, pools.Data);
      return pools.Data;
    });
  }

  public getPoolsByAddedOrUpdated(project: Project, pool: Pool | Pool[], forceRefresh: boolean = false): Observable<Pool[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const pools: Pool[] = await db.getAll(MmProjectDbCollections.pools as never);
      // const reLoadedPools = await this.poolService.getPools(project.Id, constants.MaxInt).toPromise();

      if (!Array.isArray(pool))
      {
        const existingPoolIdx = pools.findIndex(l => l.Id === pool.Id);
        if (existingPoolIdx === -1) {
          /* ADD NEW POOL */
          pools.push({...pool});
        } else {
          /* UPDATE EXISTING POOL */
          pools.splice(existingPoolIdx, 1);
          pools.splice(existingPoolIdx, 0, {...pool});
        }
        await db.set(MmProjectDbCollections.pools as never, {...pool});
      }
      else {
        for (const p of pool) {
          const existingPoolIdx = pools.findIndex(l => l.Id === p.Id);
          if (existingPoolIdx === -1) {
            /* ADD NEW POOL */
            pools.push({...p});
          } else {
            /* UPDATE EXISTING POOL */
            pools.splice(existingPoolIdx, 1);
            pools.splice(existingPoolIdx, 0, {...p});
          }
          await db.set(MmProjectDbCollections.pools as never, {...p});
        }
      }



      return pools;
    });
  }

  public getPoolsByDeleted(project: Project, poolId: number, forceRefresh: boolean = false): Observable<Pool[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const pools: Pool[] = await db.getAll(MmProjectDbCollections.pools as never);
      // const reLoadedPools = await this.poolService.getPools(project.Id, constants.MaxInt).toPromise();
      const existingPoolIdx = pools.findIndex(l => l.Id === poolId);

      if (existingPoolIdx !== -1) {
        /* REMOVE DELETED POOL */
        pools.splice(existingPoolIdx, 1);
      }

      await db.remove(MmProjectDbCollections.pools as never, poolId);
      return pools;
    });
  }

  public getDocumentTypes(project: Project, forceRefresh: boolean = false): Observable<DocumentType[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let dts;
      if (!forceRefresh) {
        dts = await db.getAll(MmProjectDbCollections.documentTypes as never);
        return dts ;
      }
      dts = await this.documentTypeService.getDocumentType().toPromise();

      await db.setAll(MmProjectDbCollections.documentTypes as never, dts);
      return dts;
    });
  }

  public getCompanies(project: Project, forceRefresh: boolean = false): Observable<Company[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let dts;
      if (!forceRefresh) {
        dts = await db.getAll(MmProjectDbCollections.companies as never);
        return dts ;
      }
      dts = (await this.companiesService.getProjectCompanies(project.Id, constants.MaxInt).toPromise())?.Data ?? [];
      await db.setAll(MmProjectDbCollections.companies as never, dts);
      return dts;
    });
  }

  // TODO: update request to new endpoint / new param to only get unfetched companies
  public getGlobalNotAssignedCompanies(project: Project, forceRefresh: boolean = false): Observable<Company[]> {
    return defer(async () => {
      return ((await this.companiesService.getNotAssignedCompanies(project, constants.MaxInt, 0).toPromise()).Data);
    });
  }


  public getUserDefineds(project: Project, forceRefresh: boolean = false): Observable<UserDefineds> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if (!forceRefresh) {
        const types = await db.getAll(MmProjectDbCollections.userDefineds as never);
        const saved = new UserDefineds();
        saved.Type1 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type1');
        saved.Type2 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type2');
        // console.info('UserDefineds from idb...');
        // console.info(saved);
        return saved;
      }

      // Download user defineds
      // console.info('forced loading UserDefineds...');
      const userDefineds = await this.userDefinedService.getUserDefineds(project.Id).toPromise();
      await db.setAll(MmProjectDbCollections.userDefineds as never, []);
      if (userDefineds) {
        await Promise.all(Object.keys(userDefineds).map(async key =>
          db.set(MmProjectDbCollections.userDefineds as never, userDefineds[key], key))
        );
      }
      return userDefineds;
    });
  }

  public deleteUserdefineds(project: Project, deletedIds, type: number) {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      const types = await db.getAll(MmProjectDbCollections.userDefineds as never);
      const userDefineds = new UserDefineds();
      userDefineds.Type1 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type1');
      userDefineds.Type2 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type2');

      const userDefinedsInternal = type == 1 ? userDefineds.Type1 : userDefineds.Type2;

      for (const deletedId of deletedIds) {
        const foundIndex = userDefinedsInternal.findIndex(x => x.Id === deletedId);
        if (foundIndex !== -1) {
          userDefinedsInternal.splice(foundIndex, 1);
        }
      }

      await db.setAll(MmProjectDbCollections.userDefineds as never, []);
      if (userDefineds) {
        await Promise.all(Object.keys(userDefineds).map(async key =>
          db.set(MmProjectDbCollections.userDefineds as never, userDefineds[key], key))
        );
      }
      return userDefineds;

    });
  }

  public updateProject(project: Project) {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const projectInternal = await db.getAll(MmProjectDbCollections.project as never);
      const found = projectInternal.find(x => x.Id == project.Id);
      if (found) {
        db.set(MmProjectDbCollections.project as never, project);
      }
      return project;
    });
  }

  public updateUserDefineds(project: Project, entry: UserDefined, isNew: boolean, type: number) {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      const types = await db.getAll(MmProjectDbCollections.userDefineds as never);
      const userDefineds = new UserDefineds();
      userDefineds.Type1 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type1');
      userDefineds.Type2 = await db.get(MmProjectDbCollections.userDefineds as never, 'Type2');

      // const userDefineds = await this.userDefinedService.getUserDefineds(project.Id).toPromise();

      const userDefinedsInternal = type == 1 ? userDefineds.Type1 : userDefineds.Type2;

      const found = userDefinedsInternal.find(x => x.Id == entry.Id);
      if (found) {
        found.Name1 = entry.Name1;
        found.Name2 = entry.Name2;
      }
      else {
        userDefinedsInternal.push(entry);
      }

      await db.setAll(MmProjectDbCollections.userDefineds as never, []);
      if (userDefineds) {
        await Promise.all(Object.keys(userDefineds).map(async key =>
          db.set(MmProjectDbCollections.userDefineds as never, userDefineds[key], key))
        );
      }
      return userDefineds;

    });
  }

  public getClassifications(project: Project, forceRefresh: boolean = false): Observable<Classifications> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if (!forceRefresh) {
        const cs = await db.getAll(MmProjectDbCollections.classifications as never);
        const saved = new Classifications();
        saved.Class1 = await db.get(MmProjectDbCollections.classifications as never, 'Class1');
        saved.Class2 = await db.get(MmProjectDbCollections.classifications as never, 'Class2');
        saved.Class3 = await db.get(MmProjectDbCollections.classifications as never, 'Class3');
        saved.Class4 = await db.get(MmProjectDbCollections.classifications as never, 'Class4');
        return saved;
      }

      // Download classifications
      const classifications = await this.classificationService.getProjectClassifications(project.Id).toPromise();
      await db.setAll(MmProjectDbCollections.classifications as never, []);
      if (classifications) {
        await Promise.all(Object.keys(classifications).map(async key =>
          db.set(MmProjectDbCollections.classifications as never, classifications[key], key))
        );
      }
      return classifications;
    });
  }

  public getPermissions(project: Project, forceRefresh: boolean = false): Observable<ProjectUserPermissions> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let  permissions: ProjectUserPermissions = {};
      if (!forceRefresh) {
        permissions.Actions     = await db.get(MmProjectDbCollections.userPermissions  as never, 'Actions');
        permissions.AdminActions     = await db.get(MmProjectDbCollections.userPermissions  as never, 'AdminActions');
        permissions.Configs     = await db.get(MmProjectDbCollections.userPermissions as never, 'Configs');
        permissions.Shortcuts     = await db.get(MmProjectDbCollections.userPermissions as never, 'Shortcuts');
        permissions.ProjectRole = await db.get(MmProjectDbCollections.userPermissions as never, 'ProjectRole');
        permissions.User        = await db.get(MmProjectDbCollections.userPermissions as never, 'User');

        return permissions;
      }

      // Download user permissions
      permissions = await this.permissionsService.getProjectPermissions(project.Id).toPromise();
      await db.setAll(MmProjectDbCollections.userPermissions as never, []);
      if (permissions) {
        await Promise.all(Object.keys(permissions).map(key =>
          db.set(MmProjectDbCollections.userPermissions as never, permissions[key], key))
        );
      }
      return permissions;
    });
  }



  public getContacts(project: Project, forceRefresh: boolean = false): Observable<Contact[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.contacts as never);
      }

      // Download contacts
      const contacts = await this.contactService.getProjectContacts(project.Id, constants.MaxInt).toPromise();
      await db.setAll(MmProjectDbCollections.contacts as never, contacts && contacts.Data ? contacts.Data : []);
      return contacts.Data;
    });
  }


  public getCrafts(project: Project, forceRefresh: boolean = false): Observable<Craft[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.crafts as never);
      }

      // Download crafts
      const crafts = await this.craftService.getProjectCrafts(project.Id, constants.MaxInt).toPromise();
      await db.setAll(MmProjectDbCollections.crafts as never, crafts && crafts.Data ? crafts.Data : []);
      return crafts.Data;
    });
  }

  public getGlobalNotAssignedCrafts(project: Project, forceRefresh: boolean = false): Observable<Craft[]> {
    return defer(async () => {
      return ((await this.craftService.getNotAssignedCrafts(project, constants.MaxInt, 0).toPromise()).Data);
    });
  }

  public getOrders(project: Project, forceRefresh: boolean = false): Observable<Order[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let orders;
      if (!forceRefresh) {
        orders = await db.getAll(MmProjectDbCollections.orders as never);
        return orders;
      }
      orders = await this.orderService.getProjectOrders(project.Id, constants.MaxInt).toPromise();

      await db.setAll(MmProjectDbCollections.orders as never, orders && orders.Data ? orders.Data : []);
      return orders.Data;
    });
  }

  public getStatuses(project: Project, forceRefresh: boolean = false): Observable<Status[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.statuses as never);
      }
      const statuses = await this.statusService.getProjectStatusList(project.Id, 1000).toPromise();
      await db.setAll(MmProjectDbCollections.statuses as never, statuses && statuses.Data ? statuses.Data : []);

      return statuses.Data;
    });
  }


  public getFields(project: Project, forceRefresh: boolean = false): Observable<FieldMinimal[]> {
    return defer(async () => {

      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let fields;
      if (!forceRefresh) {
        fields = await db.getAll(MmProjectDbCollections.fields as never);
        return fields;
      }
      //
      const loaded = await this.fieldService.getProjectFields(project.Id, true,
        [...this.fieldService.groupFields, 'Craft_Name2', 'Company_Name2', 'Classification1_Name2', 'Classification2_Name2', 'Classification3_Name2', 'Classification4_Name2'],
        ['AttributeGroup_Image', 'AttributeGroup_Name1', 'Attribute_Name1', 'Structure_Name1', 'Structure_Name2', 'Structure_LongPath', 'Structure_ShortPath', 'Structure_DisplayPath', 'ResponsibleCO_Company', 'ResponsibleCT_Company', 'ResponsibleSC_Company',
        'LastActivitySC_StatusGroupSC', 'LastActivityCO_StatusGroupSC', 'LastActivityCO_StatusGroupCT', 'LastActivityCT_StatusGroupCT',
        'LastActivityCT_Status_Name1', 'LastActivitySC_Status_Name1', 'LastActivitySC_Description', 'LastActivityCT_Description', 'DelayGroupSC_Id', 'DelayGroupCT_Id', 'ProjectSpecific_Name2', 'ProjectSpecific2_Name2'], 10000).toPromise();

      const structureLongPath = loaded.Data.find(x => x.Id === 'Structure_LongPath');

      if (loaded) {
        const structureField: FieldMinimal = {
          Id: 'Structure',
          Global: structureLongPath.Project || structureLongPath.Global,
          Permission: 1,
          Prop: 'Structure'
        };
        loaded.Data.push(structureField);
      }
      fields = loaded && loaded.Data ? loaded.Data : [];
      await db.setAll(MmProjectDbCollections.fields as never, fields);
      return fields;
    });
  }

  public async saveStructures(project: Project, structures: Structure[]): Promise<void> {
    const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
    await db.setAll(MmProjectDbCollections.structures as never, structures);
  }

  public getStructures(project: Project, forceRefresh: boolean = false): Observable<Structure[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      let structures;
      if (!forceRefresh) {
        const rawStructures = await db.getAll(MmProjectDbCollections.structures as never);
        structures = rawStructures.map(s => new Structure(s));
        return structures;
      }
      const loaded = await this.structureService.getProjectStructures(project.Id).toPromise();
      structures = this.structureService.loadStructureTree(loaded.Data);
      await db.setAll(MmProjectDbCollections.structures as never, structures);
      return structures;
    });
  }

  getPlans(project: Project, structures: Structure[], forceRefresh: boolean): Observable<PlanData[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let plans;
      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.plans as never);
      }
      const loaded = await this.planService.getPlans(project, structures).toPromise();
      plans = loaded;
      if (this.platformModeStateService.isLocalModeEnabled){
        await Promise.all(plans.map(async p => {
            const updatedAt = p?.BpPlan?.updatedAt ?? p.ModifiedOn;
            const needsUpdate = await this.offlineService.checkNeedsUpdate(p.Id, '0/0_0.jpg', new Date(updatedAt));
            if (needsUpdate){
              if (!p?.BpPlan){
                await this.planService.getPlanTilesMetaData(p);
              }
              await this.planService.downloadBaseTiles(p);
            }
        }));
      }
      await db.setAll(MmProjectDbCollections.plans as never, loaded);
      return plans;
    });
  }

  public getProjectSettings(project: Project, forceRefresh: boolean): Observable<ProjectSetting[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.projectSettings as never);
      }
      const projectSettings = await this.projectSettingsService.getProjectSettings(project.Id, 10000).toPromise();
      await db.setAll(MmProjectDbCollections.projectSettings as never, projectSettings);

      return projectSettings;
    });
  }

  public getAttributes(project: Project, forceRefresh: boolean = false): Observable<Attribute[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.attributes as never);
      }
      const attributes = await this.attributeService.getProjectAttributeList(project.Id, 1000).toPromise();
      await db.setAll(MmProjectDbCollections.attributes as never, attributes && attributes.Data ? attributes.Data : []);

      return attributes.Data;
    });
  }

  public getAttributeGroups(project: Project, forceRefresh: boolean = false): Observable<{attributeGroup: AttributeGroup[], attributeGroupFiles: AttributeGroupFile[]}> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      if (!forceRefresh) {
        return {
          attributeGroup:  await db.getAll(MmProjectDbCollections.attributeGroups as never),
          attributeGroupFiles: await db.getAll(MmProjectDbCollections.attributeGroups_files as never)
        };
      }
      const attributeGroups = await this.attributeGroupService.getProjectAttributeGroupList(project.Id, 1000).toPromise();
      const attributeGroupsFiles: AttributeGroupFile[] = [];
      await Promise.all(attributeGroups.Data.map(async attributeGroup => {
        try {
          const attributeGroupFile = await firstValueFrom(await this.attributeGroupService.getProjectAttributeGroupFile(attributeGroup.Id, project.Id));
          attributeGroupFile.file.Id = attributeGroup.Id;
          attributeGroupFile.file.ImageFileName = attributeGroup.ImageFileName;
          // attributeGroupFile.file.buffer = await attributeGroupFile.blob.arrayBuffer();
          attributeGroupsFiles.push(attributeGroupFile.file);
          return Promise.resolve(attributeGroupFile);
        }
        catch (err) {
          this.logger.error(err, '', 'MmProjectOfflineService');
        }
      }));

      await db.setAll(MmProjectDbCollections.attributeGroups as never, attributeGroups && attributeGroups.Data ? attributeGroups.Data : []);
      await db.setAll(MmProjectDbCollections.attributeGroups_files as never, attributeGroupsFiles ? attributeGroupsFiles : []);


      return {attributeGroup: attributeGroups.Data, attributeGroupFiles: attributeGroupsFiles};
    });
  }

  public getDelays(project: Project, forceRefresh: boolean = false): Observable<Delay[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      if (!forceRefresh) {
        return await db.getAll(MmProjectDbCollections.delays as never);
      }
      const delays = await this.delayApiService.getDelays(1000).toPromise();
      await Promise.all(delays.Data.map(async delay => {
        try {
          const delayFile = await firstValueFrom(await this.delayApiService.getDelayImage(delay.Id));
          delay.DelayImage = await delayFile.arrayBuffer();
          return Promise.resolve(delay);
        }
        catch (err) {
          this.logger.error(err, '', 'MmProjectOfflineService');
        }
      }));

      await db.setAll(MmProjectDbCollections.delays as never, (delays?.Data ? delays.Data : []));

      return delays.Data; // {attributeGroup: attributeGroups.Data, attributeGroupFiles: attributeGroupsFiles};
    });
  }

  public getPhases(project: Project, forceRefresh: boolean = false): Observable<Phase[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let dts;
      if (!forceRefresh) {
        dts = await db.getAll(MmProjectDbCollections.phases as never);
        return dts ;
      }
      dts = await this.phaseApiService.getPhases().toPromise();

      await db.setAll(MmProjectDbCollections.phases as never, dts);
      return dts;
    });
  }

  public getIntegrations(project: Project, forceRefresh: boolean = false): Observable<{integrations: Integration[], integrationsFiles: IntegrationFile[]}> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);

      if (!forceRefresh) {
        return {
          integrations: await db.getAll(MmProjectDbCollections.integrations as never),
          integrationsFiles: await db.getAll(MmProjectDbCollections.integrations_files as never)
        };
      }

      const integrations = await this.integrationApiService.getIntegrations().toPromise();
      const integrationsFiles: IntegrationFile[] = [];
      await Promise.all(integrations.map(async integration => {
        try {
          const integrationFile = await firstValueFrom(this.integrationApiService.getIntegrationFile(+integration.Id));
          integrationFile.file.Id = +integration.Id;
          integrationFile.file.ImageFileName = integration.ImageFileName;
          // attributeGroupFile.file.buffer = await attributeGroupFile.blob.arrayBuffer();
          integrationsFiles.push(integrationFile.file);
          return Promise.resolve(integrationsFiles);
        }
        catch (err) {
          this.logger.error(err, '', 'MmProjectOfflineService');
        }
      }));


      await db.setAll(MmProjectDbCollections.integrations as never, integrations ? integrations : []);
      await db.setAll(MmProjectDbCollections.integrations_files as never, integrationsFiles ? integrationsFiles : []);

      return {
        integrations,
        integrationsFiles
      };
    });
  }


  public async removeCompanies(project: Project, companies: Company[]) {
    const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
    await Promise.all(companies.map(async c => {
        db.remove(MmProjectDbCollections.companies as never, c.Id);
      }
    ));
  }

  public async removeContacts(project: Project, contacts: Contact[]) {
    const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
    await Promise.all(contacts.map(async c => {
        db.remove(MmProjectDbCollections.contacts as never, c.Id);
      }
    ));
  }

  public async removeOrders(project: Project, orders: Order[]) {
    const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
    await Promise.all(orders.map(async c => {
        db.remove(MmProjectDbCollections.orders as never, c.Id);
      }
    ));
  }

  public clearCompanies(project: Project): Observable<Company[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.companies as never)) > 0) {
        await db.clear(MmProjectDbCollections.companies as never);
      }
      return [];
    });
  }

  public clearCrafts(project: Project): Observable<Craft[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.crafts as never)) > 0) {
        await db.clear(MmProjectDbCollections.crafts as never);
      }
      return [];
    });
  }

  public clearOrders(project: Project): Observable<Order[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.orders as never)) > 0) {
        await db.clear(MmProjectDbCollections.orders as never);
      }
      return [];
    });
  }

  public clearContacts(project: Project): Observable<Contact[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.contacts as never)) > 0) {
        await db.clear(MmProjectDbCollections.contacts as never);
      }
      return [];
    });
  }

  public clearUserDefineds(project: Project): Observable<UserDefineds> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.userDefineds as never)) > 0) {
        await db.clear(MmProjectDbCollections.userDefineds as never);
      }
      return { Type1: [], Type2: [] } as UserDefineds;
    });
  }

  public clearClassifications(project: Project): Observable<Classifications> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      if ((await db.countAll(MmProjectDbCollections.classifications as never)) > 0) {
        await db.clear(MmProjectDbCollections.classifications as never);
      }
      return { Class1: [], Class2: [], Class3: [], Class4: [] } as Classifications;
    });
  }


}
